import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { INVESTOR_TYPES, INVESTOR_TYPE_NAMES, TENURE_OPTIONS, WITHDRAWAL_OPTIONS, RANGE_DISCLAIMER } from "../../lib/plans";
import AppLayout from "../../components/AppLayout";
import WrmSelect from "../../components/WrmSelect";
import { PasswordField, PasswordStrength, passwordStrong } from "../../components/PasswordFields";
import { Button } from "../../components/ui/button";

const TABS = ["Identity", "KYC", "Investment", "Nominee", "WRM & Channel", "Notes"];
const KYC_STATUSES = ["Pending", "Submitted", "Under Review", "Verified", "Rejected"];
const RISKS = ["Conservative", "Balanced", "Experienced"];

const In = ({ label, value, onChange, type = "text", placeholder, testId, required }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}{required && " *"}</label>
    <input type={type} value={value} placeholder={placeholder} required={required} data-testid={testId}
      onChange={(e) => onChange(e.target.value)} className="glass-input" />
  </div>
);

const Sel = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default function AdminClientNew() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    full_name: "", email: "", mobile: "", dob: "", gender: "", address: "", city: "", state: "",
    country: "India", onboarding_date: "", client_status: "Active",
    username: "", password: "", confirm_password: "",
    pan: "", aadhaar: "", kyc_status: "Pending", kyc_verified_at: "", kyc_remarks: "",
    investor_type: "", tenure_category: "", investment_objective: "",
    withdrawal_preference: "",
    nominee_name: "", nominee_relation: "", nominee_dob: "", nominee_mobile: "", nominee_email: "", nominee_share_pct: "100",
    wrm_user_id: "", region: "", channel: "", relationship_status: "Active",
    notes_internal: "", notes_preferences: "", notes_operational: "", notes_special: "",
  });

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.full_name || !f.email || !f.mobile) {
      toast.error("Full name, email and mobile are required (Identity tab).");
      setTab(0);
      return;
    }
    if (!f.username || !passwordStrong(f.password) || f.password !== f.confirm_password) {
      toast.error("Login Credentials (Identity tab): enter a Login ID and a strong matching password (8+ chars, at least one letter and number).");
      setTab(0);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        full_name: f.full_name, email: f.email, mobile: f.mobile,
        username: f.username, password: f.password,
        dob: f.dob || null, gender: f.gender || null, address: f.address || null,
        city: f.city || null, state: f.state || null, country: f.country,
        onboarding_date: f.onboarding_date || null, client_status: f.client_status,
        pan: f.pan || null, aadhaar: f.aadhaar || null, kyc_status: f.kyc_status,
        kyc_verified_at: f.kyc_verified_at || null, kyc_remarks: f.kyc_remarks || null,
        investor_type: f.investor_type || null, tenure_category: f.tenure_category || null,
        investment_objective: f.investment_objective || null,
        withdrawal_preference: f.withdrawal_preference || null,
        nominee: f.nominee_name ? {
          name: f.nominee_name, relation: f.nominee_relation, dob: f.nominee_dob,
          mobile: f.nominee_mobile, email: f.nominee_email,
          share_pct: Number(f.nominee_share_pct) || 100,
        } : {},
        wrm_user_id: f.wrm_user_id || null, region: f.region || null, channel: f.channel || null,
        relationship_status: f.relationship_status,
        notes: {
          internal: f.notes_internal, preferences: f.notes_preferences,
          operational: f.notes_operational, special_instructions: f.notes_special,
        },
      };
      const r = await api.post("/admin/investors", payload);
      toast.success("Client created. Add holdings, transactions and documents from the client profile.");
      navigate(`/admin/clients/${r.data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout title="Add New Client" subtitle="Master client onboarding — one authoritative record across Admin, WRM and Investor portals.">
      <div className="fade-up max-w-5xl space-y-6">
        <button onClick={() => navigate("/admin/clients")} data-testid="back-to-clients"
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-200">
          <ArrowLeft size={15} /> Back to Clients
        </button>

        <div className="flex flex-wrap gap-2" data-testid="onboarding-tabs">
          {TABS.map((t, i) => (
            <button key={t} data-testid={`onboard-tab-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setTab(i)}
              className={`text-xs font-semibold rounded-full px-4 py-2 border transition-colors duration-200 ${
                tab === i ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200"
              }`}>
              {t}
            </button>
          ))}
        </div>

        <div className="glass-card p-7">
          {tab === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tab-identity">
              <In label="Full Name" value={f.full_name} onChange={set("full_name")} required testId="f-full-name" />
              <In label="Email" type="email" value={f.email} onChange={set("email")} required testId="f-email" />
              <In label="Mobile" value={f.mobile} onChange={set("mobile")} required testId="f-mobile" />
              <In label="Date of Birth" type="date" value={f.dob} onChange={set("dob")} testId="f-dob" />
              <Sel label="Gender" value={f.gender} onChange={set("gender")} options={["", "Male", "Female", "Other"]} testId="f-gender" />
              <In label="Onboarding Date" type="date" value={f.onboarding_date} onChange={set("onboarding_date")} testId="f-onboarding-date" />
              <In label="Address" value={f.address} onChange={set("address")} testId="f-address" />
              <In label="City" value={f.city} onChange={set("city")} testId="f-city" />
              <In label="State" value={f.state} onChange={set("state")} testId="f-state" />
              <In label="Country" value={f.country} onChange={set("country")} testId="f-country" />
              <Sel label="Client Status" value={f.client_status} onChange={set("client_status")}
                options={["Active", "Inactive", "On Hold", "Closed"]} testId="f-client-status" />
              <div className="sm:col-span-2 lg:col-span-3 border-t border-white/[0.06] pt-5 mt-1">
                <p className="label-caps mb-4" data-testid="login-creds-heading">Login Credentials — portal access for this client</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  <In label="Client Login User ID" value={f.username} onChange={set("username")} placeholder="KMC-CL-001" required testId="f-username" />
                  <PasswordField label="Client Password" value={f.password} onChange={set("password")} testId="f-password" />
                  <PasswordField label="Confirm Password" value={f.confirm_password} onChange={set("confirm_password")} testId="f-confirm-password" />
                  <div className="sm:col-span-2 lg:col-span-3 space-y-1.5">
                    <PasswordStrength password={f.password} testId="f-password-strength" />
                    {f.confirm_password && f.password !== f.confirm_password && (
                      <p className="text-[11px] text-red-400" data-testid="f-password-mismatch">Passwords do not match.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {tab === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tab-kyc">
              <In label="PAN" value={f.pan} onChange={set("pan")} placeholder="ABCDE1234F" testId="f-pan" />
              <In label="Aadhaar" value={f.aadhaar} onChange={set("aadhaar")} placeholder="XXXX XXXX 1234" testId="f-aadhaar" />
              <Sel label="KYC Status" value={f.kyc_status} onChange={set("kyc_status")} options={KYC_STATUSES} testId="f-kyc-status" />
              <In label="KYC Verification Date" type="date" value={f.kyc_verified_at} onChange={set("kyc_verified_at")} testId="f-kyc-date" />
              <div className="sm:col-span-2">
                <In label="KYC Remarks" value={f.kyc_remarks} onChange={set("kyc_remarks")} testId="f-kyc-remarks" />
              </div>
            </div>
          )}
          {tab === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tab-investment">
              <Sel label="Investor Type" value={f.investor_type} testId="f-investor-type"
                onChange={(v) => setF((p) => ({ ...p, investor_type: v }))}
                options={["", ...INVESTOR_TYPE_NAMES]} />
              <div className="space-y-1.5">
                <label className="label-caps">Series (auto)</label>
                <p className="glass-input flex items-center text-sm text-emerald-300" data-testid="f-series-auto">
                  {f.investor_type ? `${INVESTOR_TYPES[f.investor_type].series} Series` : "—"}
                </p>
              </div>
              <Sel label="Tenure" value={f.tenure_category} onChange={set("tenure_category")}
                options={["", ...TENURE_OPTIONS]} testId="f-tenure" />
              <div className="space-y-1.5">
                <label className="label-caps">Applicable Plan (auto)</label>
                <p className="glass-input flex items-center text-sm text-emerald-300" data-testid="f-plan-auto">
                  {f.investor_type && f.tenure_category ? INVESTOR_TYPES[f.investor_type].plans[f.tenure_category] : "—"}
                </p>
              </div>
              <Sel label="Withdrawal Option" value={f.withdrawal_preference} onChange={set("withdrawal_preference")}
                options={["", ...WITHDRAWAL_OPTIONS]} testId="f-withdrawal" />
              <div className="sm:col-span-2">
                <In label="Investment Objective" value={f.investment_objective} onChange={set("investment_objective")} testId="f-objective" />
              </div>
              {f.investor_type && (
                <p className="sm:col-span-2 lg:col-span-3 text-[11px] text-emerald-300/80 leading-relaxed" data-testid="f-range-note">
                  Indicative Return Range: {INVESTOR_TYPES[f.investor_type].range} · Risk appetite: {INVESTOR_TYPES[f.investor_type].riskAppetite} · {RANGE_DISCLAIMER}
                </p>
              )}
            </div>
          )}
          {tab === 3 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tab-nominee">
              <In label="Nominee Name" value={f.nominee_name} onChange={set("nominee_name")} testId="f-nom-name" />
              <In label="Relationship" value={f.nominee_relation} onChange={set("nominee_relation")} testId="f-nom-relation" />
              <In label="Date of Birth" type="date" value={f.nominee_dob} onChange={set("nominee_dob")} testId="f-nom-dob" />
              <In label="Mobile" value={f.nominee_mobile} onChange={set("nominee_mobile")} testId="f-nom-mobile" />
              <In label="Email" type="email" value={f.nominee_email} onChange={set("nominee_email")} testId="f-nom-email" />
              <In label="Share %" type="number" value={f.nominee_share_pct} onChange={set("nominee_share_pct")} testId="f-nom-share" />
            </div>
          )}
          {tab === 4 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tab-wrm">
              <WrmSelect value={f.wrm_user_id} onChange={set("wrm_user_id")} testId="f-wrm" />
              <In label="Region" value={f.region} onChange={set("region")} placeholder="South India" testId="f-region" />
              <In label="Channel" value={f.channel} onChange={set("channel")} placeholder="Direct" testId="f-channel" />
              <Sel label="Relationship Status" value={f.relationship_status} onChange={set("relationship_status")}
                options={["Active", "Inactive", "On Hold"]} testId="f-rel-status" />
            </div>
          )}
          {tab === 5 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="tab-notes">
              {[["Internal Notes", "notes_internal", "f-notes-internal"], ["Client Preferences", "notes_preferences", "f-notes-prefs"],
                ["Operational Notes", "notes_operational", "f-notes-ops"], ["Special Instructions", "notes_special", "f-notes-special"]].map(([label, key, tid]) => (
                <div key={key} className="space-y-1.5">
                  <label className="label-caps">{label}</label>
                  <textarea rows={4} value={f[key]} onChange={(e) => setF((p) => ({ ...p, [key]: e.target.value }))}
                    data-testid={tid} className="glass-input h-auto py-2.5 resize-none" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <Button variant="outline" data-testid="onboard-prev" disabled={tab === 0} onClick={() => setTab(tab - 1)}
            className="border-white/10 text-zinc-300 hover:bg-white/5">
            Previous
          </Button>
          {tab < TABS.length - 1 ? (
            <Button data-testid="onboard-next" onClick={() => setTab(tab + 1)}
              className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
              Continue
            </Button>
          ) : (
            <Button data-testid="onboard-submit" disabled={busy} onClick={submit}
              className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold emerald-glow">
              <Check size={16} className="mr-1.5" weight="bold" /> {busy ? "Creating..." : "Create Client"}
            </Button>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
